<template>
    <div class="fc">
        <transition name="fc-rise">
            <section
                v-if="open"
                class="fc__console"
                role="dialog"
                :aria-label="$t('feedback.title')"
                @keydown.esc.stop="close"
            >
                <header class="fc__head">
                    <span class="fc__eyebrow">{{ $t('feedback.eyebrow') }}</span>
                </header>

                <div v-if="state !== 'sent'" class="fc__body">
                    <div class="fc__channels" role="radiogroup" :aria-label="$t('feedback.channelLabel')">
                        <button
                            v-for="option in channels"
                            :key="option.value"
                            type="button"
                            class="fc__channel"
                            :class="[`fc__channel--${option.value}`, { 'is-on': channel === option.value }]"
                            role="radio"
                            :aria-checked="channel === option.value ? 'true' : 'false'"
                            @click="channel = option.value"
                        >{{ $t(option.labelKey) }}</button>
                    </div>

                    <label class="visually-hidden" for="fc-message">{{ $t('feedback.title') }}</label>
                    <!-- One prompt, one caret. The caret is the textarea's own,
                         recoloured — a second blinking thing anywhere on this
                         panel would compete with where the player is typing. -->
                    <div class="fc__field" :class="{ 'is-sending': state === 'sending' }">
                        <span class="fc__prompt" aria-hidden="true">&gt;</span>
                        <textarea
                            id="fc-message"
                            ref="input"
                            v-model="message"
                            class="fc__input"
                            rows="5"
                            :maxlength="maxLength"
                            :placeholder="$t(`feedback.placeholder.${channel}`)"
                            :disabled="state === 'sending'"
                            @keydown.enter.meta.prevent="send"
                            @keydown.enter.ctrl.prevent="send"
                        ></textarea>
                        <span class="fc__sweep" aria-hidden="true"></span>
                    </div>

                    <p v-if="state === 'failed'" class="fc__alert" role="alert">{{ $t('feedback.failed') }}</p>

                    <footer class="fc__foot">
                        <span class="fc__meter" aria-hidden="true">
                            <span class="fc__meter-fill" :style="{ transform: `scaleX(${used})` }"></span>
                        </span>
                        <span v-if="showCount" class="fc__count">{{ $t('feedback.remaining', { count: remaining }) }}</span>
                        <HudButton :type="ButtonType.CANCEL" @click="close">{{ $t('feedback.cancel') }}</HudButton>
                        <HudButton
                            :type="ButtonType.SUBMIT"
                            :disabled="!canSend"
                            @click="send"
                        >{{ state === 'sending' ? $t('feedback.sending') : $t('feedback.send') }}</HudButton>
                    </footer>
                </div>

                <!-- The one moment the console performs: the answer is typed
                     back rather than shown. -->
                <div v-else class="fc__receipt">
                    <ConsoleText :text="$t('feedback.sent')" :speed="26"/>
                </div>
            </section>
        </transition>

        <FeedbackTrigger class="fc__corner"/>
    </div>
</template>

<script>
/*
 * The feedback console: a signed-in player writes to the people who maintain
 * the game without leaving the screen they are on.
 *
 * Mounted once, app-wide. It carries the route it was opened from — the one
 * piece of context that makes a report actionable and that a player should
 * never have to describe in prose.
 */
import ConsoleText from '@/components/Player/ConsoleText.vue';
import FeedbackTrigger from '@/components/Feedback/FeedbackTrigger.vue';
import HudButton from '@/components/btn/HudButton.vue';
import {ButtonType} from '@/types/ButtonType';
import feedbackConsole from '@/services/feedbackConsole';
import feedbackService, {FEEDBACK_CHANNELS, FEEDBACK_MESSAGE_MAX_LENGTH} from '@/services/feedbackService';

const CLOSE_AFTER_SENT_MS = 2600;

export default {
    name: 'FeedbackConsole',
    components: {ConsoleText, FeedbackTrigger, HudButton},
    props: {
        service: {type: Object, default: () => feedbackService},
    },
    data() {
        return {
            channel: 'signal',
            message: '',
            /* idle | sending | sent | failed */
            state: 'idle',
            channels: FEEDBACK_CHANNELS,
            maxLength: FEEDBACK_MESSAGE_MAX_LENGTH,
            ButtonType,
            closeTimer: null,
        };
    },
    computed: {
        open() {
            return feedbackConsole.isOpen;
        },
        remaining() {
            return this.maxLength - this.message.length;
        },
        /* A count reading "2000 left" over an empty box is noise. It appears
           only once the room left is worth knowing. */
        showCount() {
            return this.remaining <= 200;
        },
        used() {
            return Math.min(1, this.message.length / this.maxLength);
        },
        canSend() {
            return this.state !== 'sending' && this.message.trim().length > 0;
        },
    },
    watch: {
        open(isOpen) {
            if (!isOpen) {
                this.clearCloseTimer();
                this.state = 'idle';
                return;
            }
            this.clearCloseTimer();
            this.state = 'idle';
            this.$nextTick(() => this.$refs.input?.focus());
        },
    },
    beforeUnmount() {
        this.clearCloseTimer();
    },
    methods: {
        close() {
            feedbackConsole.close();
        },
        clearCloseTimer() {
            if (this.closeTimer) clearTimeout(this.closeTimer);
            this.closeTimer = null;
        },
        currentRoute() {
            return this.$route?.fullPath || '';
        },
        async send() {
            if (!this.canSend) return;
            this.state = 'sending';
            try {
                await this.service.send({
                    message: this.message,
                    channel: this.channel,
                    route: this.currentRoute(),
                });
            } catch (error) {
                /* The words stay in the box: a failed send must never be a
                   reason to type them again. */
                this.state = 'failed';
                return;
            }
            this.message = '';
            this.state = 'sent';
            this.closeTimer = setTimeout(() => this.close(), CLOSE_AFTER_SENT_MS);
        },
    },
};
</script>

<style scoped>
.fc {
    --fc-cyan: #3ee9f0;
    --fc-amber: #f0b866;
    --fc-slab: rgba(9, 14, 19, 0.94);
    --fc-line: rgba(62, 233, 240, 0.22);
    --fc-line-lit: rgba(62, 233, 240, 0.6);
    --fc-text: #d3e0e6;
    --fc-dim: #7f929c;

    position: fixed;
    right: 0;
    bottom: 0;
    /* Above every game surface: the console is only ever on screen because the
       player asked for it, and it must not end up behind the bar it was opened
       from. */
    z-index: 5000;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    pointer-events: none;
}

.fc > * {
    pointer-events: auto;
}

.fc__corner {
    margin: 0 0.6rem 0.6rem 0;
}

/* ---- console -------------------------------------------------------- */
.fc__console {
    /* Always the same place: the bottom-right of the screen, over everything.
       Fixed to the viewport rather than hung off the button, so it cannot be
       pushed off screen by wherever the bar happens to sit. */
    position: fixed;
    right: 0.75rem;
    /* Clear of the button it opens, which keeps the corner. */
    bottom: 2.4rem;
    width: min(23rem, calc(100vw - 1.5rem));
    background: var(--fc-slab);
    border: 1px solid var(--fc-line-lit);
    border-radius: 0.35rem;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.6), 0 1.2rem 2.5rem rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(6px);
}

.fc__head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--fc-line);
}

.fc__eyebrow {
    font-family: 'Orbitron', system-ui, sans-serif;
    font-size: 0.5rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: var(--fc-cyan);
}

.fc__body,
.fc__receipt {
    padding: 0.7rem 0.75rem 0.75rem;
}

.fc__channels {
    display: flex;
    gap: 0.3rem;
    margin-bottom: 0.55rem;
}

.fc__channel {
    flex: 1;
    padding: 0.3rem 0.2rem;
    font-family: 'Orbitron', system-ui, sans-serif;
    font-size: 0.5rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--fc-dim);
    background: transparent;
    border: 1px solid var(--fc-line);
    border-radius: 0.2rem;
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.fc__channel:hover {
    color: var(--fc-text);
}

.fc__channel.is-on {
    color: var(--fc-cyan);
    background: rgba(62, 233, 240, 0.1);
    border-color: var(--fc-line-lit);
}

/* A fault is the one channel that is not routine, so it is the one thing on
   the panel that is not cyan. */
.fc__channel--fault.is-on {
    color: var(--fc-amber);
    background: rgba(240, 184, 102, 0.1);
    border-color: rgba(240, 184, 102, 0.5);
}

.fc__channel:focus-visible {
    outline: 1px solid var(--fc-cyan);
    outline-offset: 2px;
}

.fc__field {
    position: relative;
    display: flex;
    overflow: hidden;
    border: 1px solid var(--fc-line);
    border-radius: 0.2rem;
    background: repeating-linear-gradient(
        180deg,
        rgba(62, 233, 240, 0.035) 0,
        rgba(62, 233, 240, 0.035) 1px,
        transparent 1px,
        transparent 4px
    );
}

.fc__prompt {
    padding: 0.55rem 0.1rem 0 0.55rem;
    color: var(--fc-cyan);
    font-family: 'SFMono-Regular', 'JetBrains Mono', Menlo, Consolas, monospace;
    font-size: 0.78rem;
    line-height: 1.5;
    opacity: 0.7;
}

.fc__input {
    flex: 1;
    display: block;
    width: 100%;
    padding: 0.55rem 0.6rem 0.55rem 0.35rem;
    color: var(--fc-text);
    /* The only caret on the panel, in the panel's colour. */
    caret-color: var(--fc-cyan);
    font-family: 'SFMono-Regular', 'JetBrains Mono', Menlo, Consolas, monospace;
    font-size: 0.78rem;
    line-height: 1.5;
    background: transparent;
    border: none;
    resize: vertical;
}

.fc__input::placeholder {
    color: var(--fc-dim);
}

.fc__input:focus {
    outline: none;
}

.fc__field:focus-within {
    border-color: var(--fc-line-lit);
}

/* The transmission: the message is carried out of the box by a single pass of
   the reading head. Nothing else on the panel moves while it runs. */
.fc__sweep {
    position: absolute;
    inset: 0 0 auto;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--fc-cyan), transparent);
    opacity: 0;
}

.fc__field.is-sending .fc__input {
    color: var(--fc-dim);
}

.fc__field.is-sending .fc__sweep {
    animation: fc-sweep 0.9s ease-in-out infinite;
}

@keyframes fc-sweep {
    0% {
        top: 100%;
        opacity: 0;
    }
    20% {
        opacity: 1;
    }
    100% {
        top: 0;
        opacity: 0;
    }
}

.fc__alert {
    margin: 0.5rem 0 0;
    color: #e88a84;
    font-size: 0.7rem;
}

.fc__foot {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.6rem;
}

.fc__meter {
    flex: 1;
    height: 1px;
    background: var(--fc-line);
    overflow: hidden;
}

.fc__meter-fill {
    display: block;
    height: 100%;
    background: var(--fc-cyan);
    transform-origin: left center;
    transition: transform 0.2s ease;
}

.fc__count {
    font-family: 'Orbitron', system-ui, sans-serif;
    font-size: 0.48rem;
    letter-spacing: 0.1em;
    color: var(--fc-dim);
    white-space: nowrap;
}

.fc__receipt {
    color: var(--fc-cyan);
    font-family: 'SFMono-Regular', 'JetBrains Mono', Menlo, Consolas, monospace;
    font-size: 0.78rem;
    min-height: 4rem;
}

/* ---- the console arriving ------------------------------------------- */
.fc-rise-enter-active {
    transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.33, 0, 0.2, 1);
}

.fc-rise-leave-active {
    transition: opacity 0.14s ease, transform 0.14s ease;
}

.fc-rise-enter-from,
.fc-rise-leave-to {
    opacity: 0;
    transform: translateY(0.8rem) scaleY(0.96);
    transform-origin: bottom right;
}

@media (prefers-reduced-motion: reduce) {
    .fc__field.is-sending .fc__sweep {
        animation: none;
    }

    .fc-rise-enter-active,
    .fc-rise-leave-active {
        transition: opacity 0.12s linear;
    }

    .fc-rise-enter-from,
    .fc-rise-leave-to {
        transform: none;
    }
}

/* On a phone the console is the screen, not a corner of it. */
@media (max-width: 30rem) {
    .fc__console {
        right: 0.4rem;
        bottom: 2.2rem;
        width: calc(100vw - 0.8rem);
    }
}
</style>
